import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { readLatestPreAssessmentStatusFromApiPatient } from "@/lib/patient-preassessments";
import { mapApiPatientTag } from "@/lib/patient-tags/api";
import { isPatientStatus } from "@/lib/types/patients/status";
import { massToKilograms } from "./units";
const ADHERENCE_COMPONENT_KEYS = [
    "calories",
    "protein",
    "fat",
    "carbs",
    "water",
];
export function getAdherenceComponentEntries(scores) {
    if (!scores) {
        return [];
    }
    const entries = [];
    for (const key of ADHERENCE_COMPONENT_KEYS) {
        const raw = scores[key];
        if (typeof raw === "number" && Number.isFinite(raw)) {
            entries.push({ key, score: Math.round(raw) });
        }
    }
    return entries;
}
export function cn(...inputs) {
    return twMerge(clsx(inputs));
}
/** Combines patient first name and optional surname for display. */
export function formatPatientFullName(name, surname) {
    return [name === null || name === void 0 ? void 0 : name.trim(), surname === null || surname === void 0 ? void 0 : surname.trim()].filter(Boolean).join(" ");
}
export function calculateAge(birthdateStr) {
    const birthdate = new Date(birthdateStr);
    const today = new Date();
    let age = today.getFullYear() - birthdate.getFullYear();
    const monthDiff = today.getMonth() - birthdate.getMonth();
    const dayDiff = today.getDate() - birthdate.getDate();
    // If birth month/day hasn't occurred yet this year, subtract one year from age
    if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
        age--;
    }
    return age;
}
/** Positive delta → up (green); negative → down (red); null/zero → no trend arrow. */
export function getAdherenceTrendFromDelta(delta) {
    if (delta == null || delta === 0) {
        return undefined;
    }
    return delta > 0 ? "up" : "down";
}
export const mapStatus = (status) => {
    const normalized = status.toLowerCase();
    if (isPatientStatus(normalized)) {
        return normalized;
    }
    return "pending";
};
export const filterPatient = (patient, searchTerm, statusFilter, tagFilterKeys = [], getPatientTagKeys) => {
    const search = searchTerm.toLowerCase();
    const matchesSearch = patient.name.toLowerCase().includes(search);
    const matchesStatus = statusFilter === "all" ? true : patient.status === statusFilter;
    const matchesTags = tagFilterKeys.length === 0 || !getPatientTagKeys
        ? true
        : tagFilterKeys.every((key) => getPatientTagKeys(patient.id).includes(key));
    return matchesSearch && matchesStatus && matchesTags;
};
export const sortPatientsByName = (a, b) => {
    return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
};
function positiveKg(value) {
    if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
        return null;
    }
    return value;
}
function measurementValueToKg(value, unit) {
    var _a;
    const num = typeof value === "string" ? Number.parseFloat(value) : Number(value);
    if (!Number.isFinite(num) || num <= 0) {
        return null;
    }
    // An unrecognised unit is still read as kilograms, as it always has been
    // here. The backend normalises on write, so that fallback is unreachable
    // for anything stored since; it only guards older rows.
    return (_a = massToKilograms(num, unit)) !== null && _a !== void 0 ? _a : num;
}
function parseMeasurementTimestampMs(timestamp) {
    if (typeof timestamp === "number" && Number.isFinite(timestamp)) {
        // Unix seconds when value is relatively small.
        return timestamp < 1e12 ? timestamp * 1000 : timestamp;
    }
    const ms = new Date(timestamp).getTime();
    return Number.isFinite(ms) ? ms : Number.NaN;
}
const WEIGHT_CHART_MAX_POINTS = 24;
/** Dashboard sparkline from `weight_measurements_7d` (backend: last 7 days, oldest first). */
export function buildWeightChartDataFromWeekMeasurements(weights) {
    if (!(weights === null || weights === void 0 ? void 0 : weights.length)) {
        return [];
    }
    const points = weights
        .map((item) => {
        const kg = measurementValueToKg(item.value, item.unit);
        const time = parseMeasurementTimestampMs(item.timestamp);
        if (kg == null || Number.isNaN(time)) {
            return null;
        }
        return { value: kg, time };
    })
        .filter((p) => p != null)
        .sort((a, b) => a.time - b.time);
    const trimmed = points.length > WEIGHT_CHART_MAX_POINTS
        ? points.slice(-WEIGHT_CHART_MAX_POINTS)
        : points;
    return trimmed.map((p) => ({ value: p.value }));
}
/** Latest weight (kg) by max timestamp in `weight_measurements_7d`. */
export function getLatestWeightKgFromWeekMeasurements(weights) {
    if (!(weights === null || weights === void 0 ? void 0 : weights.length)) {
        return null;
    }
    let latestKg = null;
    let latestTime = Number.NEGATIVE_INFINITY;
    for (const item of weights) {
        const kg = measurementValueToKg(item.value, item.unit);
        const time = parseMeasurementTimestampMs(item.timestamp);
        if (kg == null || Number.isNaN(time) || time < latestTime) {
            continue;
        }
        latestTime = time;
        latestKg = kg;
    }
    return latestKg;
}
/** Ensures at least two points so the area chart renders. */
export function normalizeWeightChartData(data) {
    if (data.length === 0) {
        return [];
    }
    if (data.length === 1) {
        return [data[0], data[0]];
    }
    return data;
}
/** Min–max spread (kg) across week weight points. */
export function getWeightWeekSpreadKg(values) {
    if (values.length < 2) {
        return 0;
    }
    return Math.max(...values) - Math.min(...values);
}
/** Below this spread we slightly widen Y domain (never full-height stretch). */
export const WEIGHT_SPARKLINE_LOW_SPREAD_KG = 0.5;
/** Min half-range when spread is tiny (±250 g → ~500 g window). */
const WEIGHT_SPARKLINE_MIN_HALF_SPAN_KG = 0.25;
/** Cap artificial widening for near-flat weeks. */
const WEIGHT_SPARKLINE_MAX_HALF_SPAN_KG = 0.25;
function getWeightSparklineYDomain(values) {
    const min = Math.min(...values);
    const max = Math.max(...values);
    const mid = (min + max) / 2;
    const spread = max - min;
    if (spread <= 0) {
        const pad = 0.25;
        return [mid - pad, mid + pad];
    }
    let halfSpan = spread / 2;
    if (spread < WEIGHT_SPARKLINE_LOW_SPREAD_KG) {
        halfSpan = Math.max(halfSpan, Math.min(WEIGHT_SPARKLINE_MIN_HALF_SPAN_KG, WEIGHT_SPARKLINE_MAX_HALF_SPAN_KG));
    }
    const pad = Math.max(halfSpan * 0.06, 0.025);
    return [mid - halfSpan - pad, mid + halfSpan + pad];
}
/** Sparkline: always real kg; small week spread gets a gently widened Y axis only. */
export function buildWeightSparklineConfig(data) {
    if (data.length === 0) {
        return null;
    }
    const values = data.map((d) => d.value);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const spread = max - min;
    const range = spread || 1;
    const yDomain = getWeightSparklineYDomain(values);
    const points = data.map((d, index) => ({
        actualValue: d.value,
        value: d.value,
        index,
        normalizedValue: (d.value - min) / range,
    }));
    return {
        points,
        yDomain,
        weekSpreadKg: spread,
        isFlat: spread <= 0,
    };
}
export function getWeightTrendFromChartData(data) {
    if (data.length < 2) {
        return undefined;
    }
    const diff = data[data.length - 1].value - data[0].value;
    if (diff < 0) {
        return "down";
    }
    if (diff > 0) {
        return "up";
    }
    return undefined;
}
function buildWeightChartDataForPatient(patient) {
    const fromWeek = buildWeightChartDataFromWeekMeasurements(patient.weight_measurements_7d);
    if (fromWeek.length === 0) {
        return [];
    }
    return normalizeWeightChartData(fromWeek);
}
/** (ИВ − ТВ) / (ИВ − ЦВ) × 100, clamped to [0, 100]. */
export function calculateWeightProgressPercent(initialKg, currentKg, targetKg) {
    const span = initialKg - targetKg;
    if (Math.abs(span) < 1e-9) {
        return null;
    }
    const raw = ((initialKg - currentKg) / span) * 100;
    if (!Number.isFinite(raw)) {
        return null;
    }
    return Math.min(100, Math.max(0, Math.round(raw)));
}
export function formatWeightKgDisplay(kg) {
    const rounded = Math.round(kg * 10) / 10;
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}
function toFiniteNumber(value) {
    if (typeof value !== "number" || Number.isNaN(value)) {
        return null;
    }
    return value;
}
/** Parses `logged_meals` from list API (number, fraction, or object). */
export function mapLoggedMealsFromApi(value) {
    var _a, _b, _c, _d, _e, _f;
    if (value == null) {
        return null;
    }
    if (typeof value === "number") {
        const n = toFiniteNumber(value);
        if (n == null) {
            return null;
        }
        if (n > 0 && n <= 1) {
            return { kind: "percent", value: Math.round(n * 100) };
        }
        if (n >= 0 && n <= 100) {
            return { kind: "percent", value: Math.round(n) };
        }
        return { kind: "count", value: Math.round(n) };
    }
    if (typeof value === "object") {
        const o = value;
        const daysWithLogs = toFiniteNumber(o.days_with_logs);
        const daysAnalyzed = toFiniteNumber(o.days_analyzed);
        if (daysWithLogs != null && daysAnalyzed != null && daysAnalyzed > 0) {
            return {
                kind: "ratio",
                logged: Math.round(daysWithLogs),
                expected: Math.round(daysAnalyzed),
            };
        }
        const percent = toFiniteNumber((_b = (_a = o.percent) !== null && _a !== void 0 ? _a : o.regularity) !== null && _b !== void 0 ? _b : o.regularity_percent);
        if (percent != null) {
            return mapLoggedMealsFromApi(percent);
        }
        const logged = toFiniteNumber((_d = (_c = o.logged) !== null && _c !== void 0 ? _c : o.logged_count) !== null && _d !== void 0 ? _d : o.count);
        const expected = toFiniteNumber((_f = (_e = o.expected) !== null && _e !== void 0 ? _e : o.total) !== null && _f !== void 0 ? _f : o.expected_count);
        if (logged != null && expected != null && expected > 0) {
            return {
                kind: "ratio",
                logged: Math.round(logged),
                expected: Math.round(expected),
            };
        }
        if (logged != null) {
            return { kind: "count", value: Math.round(logged) };
        }
    }
    return null;
}
export function mapLastLoggedMealFromApi(value) {
    if (value == null || typeof value !== "object") {
        return null;
    }
    const o = value;
    const title = typeof o.title === "string" ? o.title.trim() : "";
    const timestamp = typeof o.timestamp === "string" ? o.timestamp.trim() : "";
    if (!title || !timestamp || Number.isNaN(new Date(timestamp).getTime())) {
        return null;
    }
    return { title, timestamp };
}
function readLastLoggedMealFromPatient(patient) {
    var _a;
    const p = patient;
    return (_a = p.last_logged_meal) !== null && _a !== void 0 ? _a : p.lastLoggedMeal;
}
export function getMinutesSinceTimestamp(isoTimestamp, now = new Date()) {
    const date = new Date(isoTimestamp);
    if (Number.isNaN(date.getTime())) {
        return null;
    }
    return Math.max(0, Math.floor((now.getTime() - date.getTime()) / 60000));
}
export function formatLastLoggedMealTimeAgo(minutes, t) {
    if (minutes < 1) {
        return t("timeAgoJustNow");
    }
    if (minutes < 60) {
        return t("timeAgoMinutes", { minutes });
    }
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
        return t("timeAgoHours", { hours });
    }
    const days = Math.floor(hours / 24);
    return t("timeAgoDays", { days });
}
function readLoggedMealsFromPatient(patient) {
    var _a;
    const p = patient;
    return (_a = p.logged_meals) !== null && _a !== void 0 ? _a : p.loggedMeals;
}
export function formatLoggedMealsDisplay(data, t) {
    if (!data) {
        return t("noLoggedMealsData");
    }
    switch (data.kind) {
        case "percent":
            return t("loggedMeals", { percent: data.value });
        case "ratio":
            return t("loggedMealsRatio", {
                logged: data.logged,
                expected: data.expected,
            });
        case "count":
            return t("loggedMealsCount", { count: data.value });
    }
}
export function normalizePatientRisks(risks) {
    if (!(risks === null || risks === void 0 ? void 0 : risks.length)) {
        return [];
    }
    return risks
        .map((r) => (typeof r === "string" ? r.trim() : ""))
        .filter((r) => r.length > 0);
}
export function mapDietAdherenceFromApi(patient) {
    const current = patient.diet_adherence_current_30d;
    const delta = patient.diet_adherence_delta_7d;
    const scores = patient.diet_adherence_component_scores;
    const hasScores = scores != null && getAdherenceComponentEntries(scores).length > 0;
    return {
        dietAdherence: current != null && !Number.isNaN(current) ? Math.round(current) : null,
        dietAdherenceDelta7d: delta !== null && delta !== void 0 ? delta : null,
        dietAdherenceComponentScores: hasScores ? scores : null,
    };
}
export function mapApiPatientToDashboardPatient(patient) {
    var _a, _b, _c, _d;
    let channelId;
    if (((_a = patient.chats) === null || _a === void 0 ? void 0 : _a.chats) && patient.chats.chats.length > 0) {
        channelId = (_b = patient.chats.chats[0]) === null || _b === void 0 ? void 0 : _b.getstream_channel_id;
    }
    const { dietAdherence, dietAdherenceDelta7d, dietAdherenceComponentScores } = mapDietAdherenceFromApi(patient);
    const initialWeightKg = positiveKg(patient.initial_weight_kg);
    const currentWeightKg = positiveKg(patient.current_weight_kg);
    const targetWeightKg = positiveKg(patient.target_weight_kg);
    const weightProgressPercent = initialWeightKg != null && currentWeightKg != null && targetWeightKg != null
        ? calculateWeightProgressPercent(initialWeightKg, currentWeightKg, targetWeightKg)
        : null;
    const latestWeekWeightKg = getLatestWeightKgFromWeekMeasurements(patient.weight_measurements_7d);
    const weight = latestWeekWeightKg != null
        ? formatWeightKgDisplay(latestWeekWeightKg)
        : "—";
    const avatarUrl = patient.avatar_url;
    const avatarPreviewUrl = patient
        .avatar_preview_url;
    const weightChartData = buildWeightChartDataForPatient(patient);
    const weightTrend = getWeightTrendFromChartData(weightChartData);
    const apiBmi = patient.bmi;
    const bmi = typeof apiBmi === "number" && Number.isFinite(apiBmi) && apiBmi > 0
        ? apiBmi.toFixed(1)
        : "—";
    const bmiTrend = weightTrend;
    const bmrKcal = typeof patient.bmr_kcal === "number" &&
        Number.isFinite(patient.bmr_kcal) &&
        patient.bmr_kcal > 0
        ? Math.round(patient.bmr_kcal)
        : null;
    const tdeeKcal = typeof patient.tdee_kcal === "number" &&
        Number.isFinite(patient.tdee_kcal) &&
        patient.tdee_kcal > 0
        ? Math.round(patient.tdee_kcal)
        : null;
    const bmr = bmrKcal != null ? `${bmrKcal} kcal` : "—";
    const tdee = tdeeKcal != null ? `${tdeeKcal} kcal` : "—";
    const apiTags = patient.tags;
    const patientTags = (_c = apiTags === null || apiTags === void 0 ? void 0 : apiTags.map(mapApiPatientTag)) !== null && _c !== void 0 ? _c : [];
    return {
        id: patient.id,
        name: formatPatientFullName(patient.basic_info.name, patient.basic_info.surname),
        DOB: new Date(patient.basic_info.DOB),
        age: calculateAge(patient.basic_info.DOB),
        gender: patient.basic_info.gender,
        dietAdherence,
        dietAdherenceDelta7d,
        dietAdherenceComponentScores,
        dietaryRestrictions: [],
        risks: normalizePatientRisks(patient.risks),
        tagKeys: patientTags.map((t) => t.key),
        patientTags,
        loggedMeals: mapLoggedMealsFromApi(readLoggedMealsFromPatient(patient)),
        lastLoggedMeal: mapLastLoggedMealFromApi(readLastLoggedMealFromPatient(patient)),
        status: mapStatus(patient.status),
        // Absent on older API versions, which means the patient is editable.
        editable: patient.editable,
        latestPreAssessmentStatus: readLatestPreAssessmentStatusFromApiPatient(patient),
        email: patient.basic_info.email,
        phone: ((_d = patient.basic_info) === null || _d === void 0 ? void 0 : _d.phone) || "N/A",
        height: "—",
        weight,
        bmi,
        bmr,
        tdee,
        bmrKcal,
        tdeeKcal,
        photoUrl: undefined,
        avatar_url: avatarUrl,
        avatar_preview_url: avatarPreviewUrl,
        channelId,
        initialWeightKg,
        currentWeightKg,
        targetWeightKg,
        weightProgressPercent,
        weightChartData,
        weightTrend,
        bmiTrend,
    };
}
